import styles from './create-new-course-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../requestUtils';
import {
  Autocomplete, Checkbox, CheckboxLabel, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, Divider, endOfDayAt4PM as endOfDayAt8PM, Froala, GrayOutlinedButton, Icon, IconButton, 
  Link, ListItemText, LoadingButton,MenuItem, Select,MultiLineTextField, SnackBarContext, startOfDayAt7AM, TextField,
} from '@frontend-workspace/react-shared-lib';
import { ICatalog } from '../../../_models/catalog';
import { IInstructor } from '../../../_models/instructor';
import { IRoom } from '../../../_models/room';
import { IEmailTemplateSurvey } from '../../../_models/email-template-survey';
import { IComputer } from '../../../_models/computer';
import { IPrice } from '../../../_models/price';
import { ISurveyTypes } from '../../../_models/survey-types';
import { convertUnixTime } from '../../helpers';
import { useTranslation } from 'react-i18next';
import ConflictCheckDiaryDialog from '../conflict-check-diary-dialog/conflict-check-diary-dialog';

/* eslint-disable-next-line */
export interface CreateNewCourseDialogProps {
  onClose: () => void,
  open: boolean,
  refreshData: () => void
}

export function CreateNewCourseDialog(props: CreateNewCourseDialogProps) {
  const { data: catalogs }: { data: ICatalog[] } = useCourseGetRequestHook("catalog/getAll", []);
  const { data: instructors }: { data: IInstructor[] } = useCourseGetRequestHook("resource/instructors", []);
  const { data: rooms }: { data: IRoom[] } = useCourseGetRequestHook("room/getAll", []);
  const { data: emailTemplatesSurveys }: { data: IEmailTemplateSurvey[] } = useCourseGetRequestHook("settings/emailTemplates/surveys", []);
  const { data: computers }: { data: IComputer[] } = useCourseGetRequestHook("resource/computers", []);
  const { data: prices }: { data: IPrice[] } = useCourseGetRequestHook("resource/prices", []);
  const { data: surveyTypes }: { data: ISurveyTypes[] } = useCourseGetRequestHook("catalog/surveyTypes", []);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [adskCourseId, setAdskCourseId] = useState<string>("");
  const [catalogId, setCatalogId] = useState<string>("");
  const [date, setDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [instructorId, setInstructorId] = useState<string>("");
  const [assistantInstructorId, setAssistantInstructorId] = useState<string>("");
  const [trainingRoomId, setTrainingRoomId] = useState<string>("");
  const [link, setLink] = useState<string>("");
  const [serveyEmailTemplateId, setServeyEmailTemplateId] = useState<string>("");
  const [computerIds, setComputerIds] = useState<string[]>([]);
  const [doNotPublish, setDoNotPublish] = useState<boolean>(false);
  const [isTotalPrice, setIsTotalPrice] = useState<boolean>(false);
  const [priceId, setPriceId] = useState<string>("");
  const [priceInfo, setPriceInfo] = useState<string>("");
  const [htmlContentIngress, setHtmlContentIngress] = useState<string>("");
  const [htmlContentDescription, setHtmlContentDescription] = useState<string>("");
  const [courseRegistrationInfo, setCourseRegistrationInfo] = useState<string>("");
  const [evaluationId, setEvaluationId] = useState<string>("");
  const [helperText, setHelperText] = useState<string>("");
  const [conflicts, setConflicts] = useState<string>('');
  const [isCheckingConflict, setIsCheckingConflict] = useState<boolean>(true);

  const handleAdskCourseIdChange = (event: ChangeEvent<HTMLInputElement>) => setAdskCourseId(event.target.value);
  const handleCatalogIdChange = (id: string) => {
    setCatalogId(id);
    const catalog = catalogs.find(c => c.id === id);
    if (catalog) {
      setHtmlContentIngress(catalog?.courseIngress);
      setHtmlContentDescription(catalog?.courseDescription);
    }
  }
  useEffect(() => {
    if (date) {
      const catalog = catalogs.find(c => c.id === catalogId);
      if (catalog) {
        const initialDate = date;
        const endDate = new Date(initialDate);
        endDate.setDate(endDate.getDate() + catalog.duration.value);
        setEndDate(endDate);
      }
    }
  }, [date, catalogId]);

  useEffect(() => {
    if (endDate) {
      const endTime = convertUnixTime(endDate.getTime() / 1000);
      const meetingRoom = rooms.find(r => r.id === trainingRoomId);
      const instructor = instructors.find(i => i.id === instructorId);
      const assistantInstructor = instructors.find(i => i.id === assistantInstructorId);
      if (endTime && date && meetingRoom && instructor) {
        const catalog = catalogs.find(c => c.id === catalogId);
        const body = {
          courseId: "nytt",
          emails: [instructor.person.email, meetingRoom.email, assistantInstructor?.person.email],
          startTime: startOfDayAt7AM(date),
          endTime: endOfDayAt8PM(endDate),
          duration: catalog?.duration,
        }
        makeCourseRequest(`Course/Registrations/CheckCalendarEventConflicts`, 'POST', body).then((result: any) => {
          if (result.length > 0) {
            setIsCheckingConflict(false);
            setConflicts(result.map((d: any) => `${d.text} \n ${d.subject} `).join('\n \n'));
          }
          else {
            setIsCheckingConflict(false);
            setConflicts('');
          }
        });
      }
    }
  }, [instructorId, assistantInstructorId, date, endDate, trainingRoomId]);

  const handleDateChange = (event: any) => {
    const newStartDate = startOfDayAt7AM(event);
    setDate(newStartDate);
    setEndDate(endOfDayAt8PM(newStartDate));
  }
  const handleServeyEmailTemplateIdChange = (event: ChangeEvent<HTMLSelectElement>) => setServeyEmailTemplateId(event.target.value);
  const handleDoNotPublish = (event: ChangeEvent<Element>, checked: boolean) => setDoNotPublish(checked);
  const handleIsTotalPrice = (event: ChangeEvent<Element>, checked: boolean) => setIsTotalPrice(checked);
  const handlePriceIdChange = (event: ChangeEvent<HTMLSelectElement>) => setPriceId(event.target.value);
  const handlePriceInfoChange = (event: ChangeEvent<HTMLInputElement>) => setPriceInfo(event.target.value);
  const handleCourseRegistrationInfoChange = (event: ChangeEvent<HTMLInputElement>) => setCourseRegistrationInfo(event.target.value);
  const handleEvaluationIdChange = (event: ChangeEvent<HTMLSelectElement>) => setEvaluationId(event.target.value);

  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const toggleConflictOpen = () => setIsConflictOpen(!isConflictOpen);


  useEffect(() => {
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric', year: 'numeric' };
    const formattedDate = date ? date.toLocaleDateString('no-NO', options).replace(/\./g, '-') : '';

    const trainingRoom = rooms.find(i => i.id === trainingRoomId);
    const link = trainingRoom?.name.split(' ').join('-');

    const trainingRoomSegment =
      (link && formattedDate) ? `${link}-` :
        (!link && formattedDate) ? `velg-sted-` :
          (link || '');

    setLink(trainingRoomSegment + '' + formattedDate)
  }, [trainingRoomId, date]);

  useEffect(() => {
    if (link && trainingRoomId && date) {
      makeCourseRequest(`idFromLink/${link}`, 'GET')
        .then((result: string) => {
          const id = result;
          if (id === link || id === "") {
            setHelperText('OK');
          }
          else {
            setLink(link)
            setHelperText(`${t("baseCourse.duplicate")}`);
          }
        }).catch((error) => console.log('Error: ' + error));
    }
  }, [link]);

  const d = date ? new Date(date) : new Date();
  const trainingRoom = rooms.find(r => r.id === trainingRoomId);
  const selectedCourse = catalogs.find(c => c.id === catalogId);

  const handleProductChange = (computerId: string, checked: boolean) => {
    if (checked) setComputerIds([...computerIds, computerId]);
    else setComputerIds(computerIds.filter(id => id !== computerId));
  };

  const missingFields = (): boolean => {
    if (isTotalPrice && !priceId) {
      return true;
    }
    if (helperText === t("baseCourse.duplicate")) {
      return true;
    }
    const requiredFieldsFilled =
      catalogId &&
      date &&
      instructorId &&
      trainingRoomId &&
      serveyEmailTemplateId;
    return requiredFieldsFilled ? false : true;
  };

  const handleAddCourse = () => {
    setLoading(true);
    if (!selectedCourse || !trainingRoom) {
      return;
    }
    const body = {
      link: link,
      catalogId: catalogId,
      calendarEventId: null,
      catalogLink: selectedCourse.link,
      adskCourseId: adskCourseId,
      courseName: selectedCourse.courseName,
      courseIngress: htmlContentIngress,
      courseDescription: htmlContentDescription,
      courseRegistrationInfo: courseRegistrationInfo,
      level: selectedCourse.level,
      date: (d.getTime()) / 1000,
      duration: selectedCourse.duration,
      address: trainingRoom.address,
      postCode: trainingRoom.postCode,
      city: trainingRoom.city,
      price: isTotalPrice ? prices.find(p => p.id === priceId) : selectedCourse.price,
      instructor: instructors.find(i => i.id === instructorId),
      assistantInstructor: instructors.find(i => i.id === assistantInstructorId),
      trainingRoom: rooms.find(r => r.id === trainingRoomId),
      computers: computers.filter(computer => computerIds?.includes(computer.id)),
      doNotPublish: doNotPublish,
      isTotalPrice: isTotalPrice,
      priceInfo: priceInfo,
      surveyid: evaluationId,
      surveyEmailTemplateId: serveyEmailTemplateId
    }
    makeCourseRequest(`course/upsert`, 'POST', body).then(result => {
      setLoading(false);
      props.refreshData();
      setSnackbarMessage({ content: t("createNewCourse.addedCourseToList"), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t("createNewCourse.couldNotAddCourseToList"), type: "error" }));
  };

  const clearInputs = () => {
    setAdskCourseId("");
    setCatalogId("");
    setDate(null);
    setInstructorId("")
    setAssistantInstructorId("");
    setTrainingRoomId("");
    setLink("");
    setServeyEmailTemplateId("");
    setComputerIds([]);
    setDoNotPublish(false);
    setIsTotalPrice(false);
    setPriceId("");
    setPriceInfo("");
    setHtmlContentIngress("");
    setHtmlContentDescription("");
    setCourseRegistrationInfo("");
    setEvaluationId("");
  };

  const submitAndClose = () => {
    handleAddCourse();
    props.onClose();
  };

  const submitAndAddNew = () => {
    handleAddCourse();
    clearInputs();
  };

  return (<>
    <Dialog
      open={props.open}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("standard.newCourse")}
      </DialogTitle>
      <DialogContent>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <TextField
                label={t("standard.adskCourseId")}
                name='adskCourseId'
                fullWidth
                sx={{ minWidth: 300 }}
                value={adskCourseId || ''}
                onChange={handleAdskCourseIdChange}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <Autocomplete
                value={catalogId || ''}
                options={catalogs}
                getOptionLabel={(catalogId: any) => {
                  const catalog = catalogs.find(c => c.id === catalogId);
                  return catalog ? catalog?.courseName : ``;
                }}
                filterOptions={(options: ICatalog[], state: any) => {
                  return options.filter(option => option.courseName.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, catalog: ICatalog) => {
                  return (
                    <div onClick={(event) => handleCatalogIdChange(catalog.id)}>
                      <MenuItem key={catalog.id} value={catalog.id}>
                        <ListItemText primary={catalog.courseName} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.courseName")} &#42;</span>}
                    hiddenLabel
                  />
                }}
              />
            </div>
          </Col>
          <Col xl={6}>
            <div className='d-flex align-items-end mb-3'>
              <div className='me-1'>
                <DatePicker
                  label={<span>{t("standard.date")} &#42;</span>}
                  inputFormat="dd.MM.yyyy"
                  value={date || null}
                  onChange={handleDateChange}
                />
              </div>
              {isCheckingConflict ? (
                <div>
                  <Icon className="material-icons-outlined" color='info'>pending</Icon>
                </div>
              ) : (
                conflicts ? (
                  <div>
                    <IconButton onClick={toggleConflictOpen}>
                      <Icon className="material-icons-outlined" color='error'>info</Icon>
                    </IconButton>
                  </div>
                ) : (
                  <div>
                    <Icon className="material-icons-outlined" color='success'>check_circle</Icon>
                  </div>
                )
              )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <Autocomplete
                value={instructorId || ''}
                options={instructors}
                getOptionLabel={(instructorId: any) => {
                  const instructor = instructors.find(i => i.id === instructorId);
                  return instructor ? `${instructor?.person.firstName} ${instructor?.person.lastName}` : instructorId;
                }}
                filterOptions={(options: IInstructor[], state: any) => {
                  return options.filter(option => `${option.person.firstName} ${option.person.lastName}`.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, instructorId: IInstructor) => {
                  return (
                    <div onClick={(event) => setInstructorId(instructorId.id)}>
                      <MenuItem key={instructorId.id} value={instructorId.id}>
                        <ListItemText primary={`${instructorId.person.firstName} ${instructorId.person.lastName}`} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.instructor")} &#42;</span>}
                    hiddenLabel
                  />
                }}
              />
            </div>
          </Col>
          <Col xl={6}>
            <div className='mb-3'>
              <Autocomplete
                value={assistantInstructorId || ''}
                options={instructors}
                getOptionLabel={(assistantInstructorId: any) => {
                  const instructor = instructors.find(i => i.id === assistantInstructorId);
                  return instructor ? `${instructor?.person.firstName} ${instructor?.person.lastName}` : assistantInstructorId;
                }}
                filterOptions={(options: IInstructor[], state: any) => {
                  return options.filter(option => `${option.person.firstName} ${option.person.lastName}`.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, assistantInstructorId: IInstructor) => {
                  return (
                    <div onClick={(event) => setAssistantInstructorId(assistantInstructorId.id)}>
                      <MenuItem key={assistantInstructorId.id} value={assistantInstructorId.id}>
                        <ListItemText primary={`${assistantInstructorId.person.firstName} ${assistantInstructorId.person.lastName}`} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.assistantInstructor")}</span>}
                    hiddenLabel
                  />
                }}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <Autocomplete
                value={trainingRoomId || ''}
                options={rooms}
                getOptionLabel={(trainingRoomId: any) => {
                  const room = rooms.find(r => r.id === trainingRoomId);
                  return room?.name || trainingRoomId;
                }}
                filterOptions={(options: IRoom[], state: any) => {
                  return options.filter(option => option.name.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, trainingRoomId: IRoom) => {
                  return (
                    <div onClick={(event) => setTrainingRoomId(trainingRoomId.id)}>
                      <MenuItem key={trainingRoomId.id} value={trainingRoomId.id}>
                        <ListItemText primary={trainingRoomId.name} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.trainingRoom")} &#42;</span>}
                    hiddenLabel
                  />
                }}
              />
            </div>
          </Col>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={
                  <span>
                    {t("standard.linkToCourse")}
                    <span style={{ color: helperText === t("baseCourse.duplicate") ? '#A30014' : '#2e7d32' }}>
                      {helperText && ` (${helperText})`}
                    </span>
                  </span>
                }
                name='link'
                fullWidth
                value={link || ''}
                disabled
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={2}>
            <div className='mb-3'>
              <TextField
                label={t("standard.maxNumberOfSeats")}
                name='link'
                fullWidth
                value={trainingRoom?.seats || ''}
                disabled
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={t("standard.address")}
                name='address'
                fullWidth
                sx={{ minWidth: 300 }}
                value={trainingRoom?.address || ''}
                disabled
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <Row>
              <Col xl={4}>
                <div className='mb-3'>
                  <TextField
                    label={t("standard.postCode")}
                    name='postCode'
                    fullWidth
                    disabled
                    value={trainingRoom?.postCode || ''}
                  />
                </div>
              </Col>
              <Col xl={8}>
                <div className='mb-3'>
                  <TextField
                    label={t("standard.place")}
                    name='city'
                    fullWidth
                    disabled
                    value={trainingRoom?.city || ''}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xl={12}>
            <div className='d-flex align-items-end flex-wrap mb-3'>
              <div className='me-3'>
                <Select
                  label={<span>{t("standard.evaluation")} &#42;</span>}
                  name='evaluation'
                  displayEmpty
                  fullWidth
                  value={evaluationId || ''}
                  onChange={handleEvaluationIdChange}
                  sx={{ minWidth: 300 }}>
                  <MenuItem value={''}>{t("standard.noEvaluation")}</MenuItem>
                  {surveyTypes.map((s) =>
                    <MenuItem key={s.id} value={s.id}>
                      {s.data.surveyinfo.title}
                    </MenuItem>
                  )}
                </Select>
              </div>
              <div>
                <Link
                  href="https://createSurvey.azurewebsites.net/"
                  target="_blank"
                  underline='always'
                  className='MuiTypography-body5'>
                  <div className='d-flex align-items-end'>
                    <Icon className='me-1'>open_in_new</Icon>{t("standard.addNewEvaluationForm")}
                  </div>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <Select
                label={<span>{t("standard.email")} &#42;</span>}
                displayEmpty
                fullWidth
                sx={{ minWidth: 300 }}
                name='serveyEmailTemplate'
                value={serveyEmailTemplateId || ''}
                onChange={handleServeyEmailTemplateIdChange}>
                {emailTemplatesSurveys.map(e => (
                  <MenuItem
                    key={e.id}
                    value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='mb-3'>
              <Autocomplete
                value={computerIds || []}
                multiple
                options={computers}
                getOptionLabel={(computerIds: any) => {
                  const computer = computers.find(ua => ua.id === computerIds);
                  return computer?.name || '';
                }}
                filterOptions={(options: IComputer[], state: any) => {
                  return options.filter(option => option.name.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, computers: IComputer) => {
                  return (
                    <div onClick={(event) => handleProductChange(computers.id, !computerIds.includes(computers.id))}>
                      <MenuItem key={computers.id} value={computers.id}>
                        <Checkbox
                          checked={computerIds.includes(computers.id)}
                          onChange={(event: any, checked: boolean) => handleProductChange(computers.id, checked)}
                        />
                        <ListItemText primary={computers.name} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.selectComputerEquipment")}</span>}
                    hiddenLabel
                  />
                }}
                onChange={(event: any, computerIds: any) => setComputerIds(computerIds)}
              />
            </div>
          </Col>
        </Row>
        <div className='d-flex mb-3'>
          <CheckboxLabel
            label={t("standard.doNotPublish")}
            checked={doNotPublish || false}
            onChange={handleDoNotPublish}
          />
        </div>
        <div className='d-flex mb-3'>
          <CheckboxLabel
            label={t("standard.courseWithTotalPrice")}
            checked={isTotalPrice || false}
            onChange={handleIsTotalPrice}
          />
        </div>
        <Row>
          <Col xl={3}>
            {isTotalPrice &&
              <div className='mb-3'>
                <Select
                  label={t("standard.selectPrice")}
                  name='price'
                  displayEmpty
                  fullWidth
                  sx={{ minWidth: 300 }}
                  value={priceId || ''}
                  onChange={handlePriceIdChange}>
                  {prices.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}>
                      {p.value}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            }
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            {isTotalPrice &&
              <div className='mb-3'>
                <TextField
                  label={t("standard.priceInformation")}
                  name='priceInformation'
                  fullWidth
                  sx={{ minWidth: 300 }}
                  value={priceInfo || null}
                  onChange={handlePriceInfoChange}
                />
              </div>
            }
          </Col>
        </Row>
        <div className='mb-3'>
          <label className='mb-1'>{t("createNewCourse.preamble")}</label>
          <Froala
            maxHeight={800}
            model={htmlContentIngress || ''}
            onModelChanged={setHtmlContentIngress}
          />
        </div>
        <div className='mb-3'>
          <label className='mb-1'>{t("standard.description")}</label>
          <Froala
            maxHeight={800}
            model={htmlContentDescription || ''}
            onModelChanged={setHtmlContentDescription}
          />
        </div>
        <div className='mb-3'>
          <MultiLineTextField
            label={t("standard.additionalInformationForParticipantUponConfirmationOfRegistration")}
            name='additionalInformation'
            fullWidth
            minRows={1}
            value={courseRegistrationInfo || ''}
            onChange={handleCourseRegistrationInfoChange}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          color='secondary'
          onClick={submitAndAddNew}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t("createNewCourse.addNewCourse")}
        </LoadingButton>
        <Divider orientation="vertical" variant="middle" flexItem className="mx-4" />
        <LoadingButton
          variant="contained"
          size='large'
          onClick={submitAndClose}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth ms-0'>
          {loading ? '' : t("standard.addCourse")}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>

    {isConflictOpen && <ConflictCheckDiaryDialog
      onClose={toggleConflictOpen}
      open={!!isConflictOpen}
      conflicts={conflicts}
    />}
  </>);

}

export default CreateNewCourseDialog;
